import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Save, Edit3, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const emptyForm = () => ({ key: '', name: '', required: true, notes: '' });
const emptyPkg = () => ({ name: '', transaction_type: '', side: '', required_forms: [emptyForm()] });

const CompliancePackagesAdmin = () => {
  const [packages, setPackages] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // package object being edited (or null)
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(emptyPkg());

  const load = async () => {
    try {
      setLoading(true);
      const [pRes, tRes] = await Promise.all([
        api.get('/compliance/packages'),
        api.get('/compliance/transaction-types'),
      ]);
      setPackages(pRes.data || []);
      setTypes(tRes.data?.transaction_types || []);
    } catch (e) {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setDraft(emptyPkg()); setCreating(true); setEditing(null); };
  const openEdit = (pkg) => {
    setDraft({
      name: pkg.name,
      transaction_type: pkg.transaction_type,
      side: pkg.side || '',
      required_forms: pkg.required_forms?.length ? pkg.required_forms.map(f => ({ ...f, notes: f.notes || '' })) : [emptyForm()],
    });
    setEditing(pkg);
    setCreating(false);
  };
  const closeEditor = () => { setEditing(null); setCreating(false); setDraft(emptyPkg()); };

  const updateForm = (idx, patch) => {
    setDraft(d => ({ ...d, required_forms: d.required_forms.map((f, i) => i === idx ? { ...f, ...patch } : f) }));
  };
  const addForm = () => setDraft(d => ({ ...d, required_forms: [...d.required_forms, emptyForm()] }));
  const removeForm = (idx) => setDraft(d => ({ ...d, required_forms: d.required_forms.filter((_, i) => i !== idx) }));

  const slugify = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

  const validate = () => {
    if (!draft.name.trim()) return 'Name required';
    if (!draft.transaction_type) return 'Transaction type required';
    for (const f of draft.required_forms) {
      if (!f.name.trim()) return 'Every form needs a name';
    }
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) return toast.error(err);
    const payload = {
      ...draft,
      side: draft.side || null,
      required_forms: draft.required_forms.map(f => ({
        key: f.key.trim() || slugify(f.name),
        name: f.name.trim(),
        required: !!f.required,
        notes: f.notes?.trim() || null,
      })),
    };
    try {
      if (editing) {
        await api.patch(`/compliance/packages/${editing.id}`, payload);
        toast.success('Package updated');
      } else {
        await api.post('/compliance/packages', payload);
        toast.success('Package created');
      }
      closeEditor();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (pkg) => {
    if (!window.confirm(`Delete package "${pkg.name}"? Existing uploaded docs stay, but new transactions with this type won't have a checklist.`)) return;
    try {
      await api.delete(`/compliance/packages/${pkg.id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const availableTypes = types.filter(t => !packages.some(p => p.transaction_type === t) || t === editing?.transaction_type);

  return (
    <Card data-testid="compliance-packages-admin">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>Compliance Packages</CardTitle>
            <CardDescription>Define required forms per transaction type. Agents see these as checklists.</CardDescription>
          </div>
          <Button onClick={openCreate} className="bg-[#BCA480] hover:bg-[#a89470] text-white" data-testid="new-package-btn">
            <Plus className="w-4 h-4 mr-2" />New Package
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {(creating || editing) && (
          <div className="border-2 border-[#BCA480]/50 rounded-lg p-4 mb-6 bg-[#faf8f4] space-y-3" data-testid="package-editor">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold">{editing ? `Edit: ${editing.name}` : 'New Package'}</h3>
              <Button variant="ghost" size="sm" onClick={closeEditor}><X className="w-4 h-4" /></Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Package Name</Label>
                <Input value={draft.name} onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))} data-testid="pkg-name" />
              </div>
              <div>
                <Label>Transaction Type</Label>
                <select className="w-full h-10 border border-gray-200 rounded px-2" value={draft.transaction_type} onChange={(e) => setDraft(d => ({ ...d, transaction_type: e.target.value }))} data-testid="pkg-type">
                  <option value="">Select…</option>
                  {(editing ? [editing.transaction_type, ...availableTypes.filter(t => t !== editing.transaction_type)] : availableTypes).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label>Side (optional)</Label>
                <select className="w-full h-10 border border-gray-200 rounded px-2" value={draft.side} onChange={(e) => setDraft(d => ({ ...d, side: e.target.value }))} data-testid="pkg-side">
                  <option value="">Either</option>
                  <option value="buyer">Buyer</option>
                  <option value="seller">Seller</option>
                </select>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Required Forms</Label>
                <Button variant="outline" size="sm" onClick={addForm} data-testid="add-form-row"><Plus className="w-3.5 h-3.5 mr-1" />Add form</Button>
              </div>
              <div className="space-y-2">
                {draft.required_forms.map((f, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`form-row-${i}`}>
                    <Input className="col-span-5" placeholder="Form name (e.g., Property Condition Disclosure)" value={f.name} onChange={(e) => updateForm(i, { name: e.target.value })} data-testid={`form-name-${i}`} />
                    <Input className="col-span-4" placeholder="Note (e.g., pre-1978 only)" value={f.notes} onChange={(e) => updateForm(i, { notes: e.target.value })} data-testid={`form-notes-${i}`} />
                    <label className="col-span-2 flex items-center gap-1 text-xs cursor-pointer">
                      <input type="checkbox" checked={f.required} onChange={(e) => updateForm(i, { required: e.target.checked })} data-testid={`form-required-${i}`} />
                      Required
                    </label>
                    <Button variant="ghost" size="sm" className="col-span-1 text-red-600" onClick={() => removeForm(i)} data-testid={`remove-form-${i}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-2 border-t">
              <Button variant="outline" onClick={closeEditor}>Cancel</Button>
              <Button onClick={save} className="bg-[#BCA480] hover:bg-[#a89470] text-white" data-testid="save-package-btn">
                <Save className="w-4 h-4 mr-2" />Save
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading…</div>
        ) : packages.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No packages yet. Click "New Package" to define one.</div>
        ) : (
          <div className="space-y-3">
            {packages.map(pkg => (
              <div key={pkg.id} className="border rounded-lg p-4 bg-white" data-testid={`package-card-${pkg.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold">{pkg.name}</h3>
                      <Badge variant="outline" className="text-xs">{pkg.transaction_type}</Badge>
                      {pkg.side && <Badge className="text-xs bg-blue-100 text-blue-800 border-blue-300 border capitalize">{pkg.side}-side</Badge>}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {(pkg.required_forms || []).length} forms · {(pkg.required_forms || []).filter(f => f.required).length} required
                    </div>
                    <ul className="mt-2 space-y-0.5">
                      {(pkg.required_forms || []).map((f, i) => (
                        <li key={i} className="text-sm text-gray-700">
                          {f.required ? <Check className="w-3 h-3 inline mr-1 text-emerald-600" /> : <span className="w-3 inline-block" />}
                          {f.name} {f.required && <span className="text-red-500">*</span>}
                          {f.notes && <span className="text-xs text-gray-500 ml-2">— {f.notes}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => openEdit(pkg)} data-testid={`edit-pkg-${pkg.id}`}><Edit3 className="w-4 h-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => remove(pkg)} className="text-red-600" data-testid={`delete-pkg-${pkg.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CompliancePackagesAdmin;
